import Dropdown, { CoDropdownItemProps } from "components/CoDropdown";
import copyText from "helpers/copyText";
import { shareUrlWithUtmParams, UtmMedium } from "helpers/pageUrl";
import { formatTweet } from "helpers/share";
import useAnalytics from "hooks/useAnalytics";
import { ShareType } from "hooks/useAnalytics/types";
import useLabels from "hooks/useLabels";
import { JSXElementConstructor, ReactElement } from "react";
import { toast } from "react-toastify";

const CLOSE_POPUP_DELAY_MILLISECONDS = 1000;
const TWITTER_SHARE_URL = "https://twitter.com/intent/tweet";

export type ShareDropdownProps = {
  answer?: string;
  children: ReactElement<any, string | JSXElementConstructor<any>>;
  description: string;
  keyTakeaway?: string;
  link?: string;
  query?: string;
  summary?: string;
  title?: string;
  onClick?: () => void;
  onAction?: (shareType: ShareType) => void;
  sharePaperEventOverride?: (shareType: ShareType) => void;
};

function ShareDropdown({
  answer,
  children,
  description,
  keyTakeaway,
  link,
  query,
  summary,
  title,
  onClick,
  onAction,
  sharePaperEventOverride,
}: ShareDropdownProps) {
  const { executeShareEvent, executeShareResultsEvent } = useAnalytics();
  const [shareLabels] = useLabels("share");

  const triggerAnalyticsEvent = (type: ShareType) => {
    if (query !== undefined) {
      executeShareResultsEvent(type);
    } else {
      if (sharePaperEventOverride) {
        sharePaperEventOverride(type);
      } else {
        executeShareEvent(type);
      }
    }
  };

  const handleClickTwitter = () => {
    const url = link || window.location.href;
    const shareUrl = shareUrlWithUtmParams(url, UtmMedium.X);
    const tweet = formatTweet(
      shareLabels,
      shareUrl,
      keyTakeaway,
      summary,
      answer,
      query
    );

    window.open(`${TWITTER_SHARE_URL}?text=${encodeURIComponent(tweet)}`);

    triggerAnalyticsEvent(ShareType.TWITTER);
    if (onAction) {
      onAction(ShareType.TWITTER);
    }
  };

  const handleClickCopyLink = () => {
    const url = link || window.location.href;
    const shareUrl = shareUrlWithUtmParams(url, UtmMedium.CLIPBOARD);
    copyText(shareUrl);

    // Delay the analytics event to ensure redux state is updated with the
    // correct result data
    setTimeout(() => triggerAnalyticsEvent(ShareType.COPY_LINK), 10);
    toast.success(shareLabels["link-copied"]);
    if (onClick) {
      onClick();
    }
    if (onAction) {
      onAction(ShareType.COPY_LINK);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      handleClickCopyLink();
    }
  };

  const dropdownItems: CoDropdownItemProps[] = [
    {
      key: ShareType.TWITTER,
      onClick: handleClickTwitter,
      leftIcon: <i className="text-xl icon-x-social" />,
      title: "Share to X",
    },
    {
      key: ShareType.COPY_LINK,
      onClick: handleClickCopyLink,
      leftIcon: <i className="text-xl icon-link" />,
      title: "Copy page link",
    },
  ];

  return (
    <Dropdown
      items={dropdownItems}
      title={title}
      onOpenChange={handleOpenChange}
    >
      {children}
    </Dropdown>
  );
}

export default ShareDropdown;
